import React from 'react';
import styled from 'styled-components';
import AlertText from '../../atoms/text/alert-text';
import LoginButton from '../../atoms/buttons/submit';
import logo from '../../assets/logos/logo-horizontal-green.svg';
import {
  withFormik,
  FormikProps,
  FormikErrors,
  Form,
  Field,
  FieldProps,
} from 'formik';

export interface FormValues {
  email?: string;
  password?: string;
  env?: string;
}

export interface OtherProps {
  className?: string;
  message: string;
  onSubmitClick: any;
  envOptions?: SelectOptions[];
  isFormSubmitting?: boolean;
}

export interface SelectOptions {
  value: string;
  label: string;
}

const InnerForm = (
  props: FormValues & OtherProps & FormikProps<FormValues>,
) => {
  const { touched, errors, isFormSubmitting, message } = props;
  const onSelectChange = e => {
    if (e && e.target) {
      props.setFieldValue('env', e.target.value);
    }
  };

  return (
    <div className={`${props.className}`}>
      <div className="logo-container">
        <img src={logo} />
      </div>
      <Form>
        {message && <div className="form-header">{message}</div>}
        <div className="form-group">
          <Field type="email" name="email" placeholder="Email" />
          <hr aria-hidden="true" className="hr-bottom" />
          {touched.email &&
            errors.email && (
              <hr aria-hidden="true" className="error-hr-bottom" />
            )}
          {touched.email &&
            errors.email && (
              <div className="error-text">
                <AlertText>{errors.email}</AlertText>
              </div>
            )}
        </div>

        <div className="form-group">
          <Field type="password" name="password" placeholder="Password" />
          <hr aria-hidden="true" className="hr-bottom" />
          {touched.password &&
            errors.password && (
              <hr aria-hidden="true" className="error-hr-bottom" />
            )}
          {touched.password &&
            errors.password && (
              <div className="error-text">
                <AlertText>{errors.password}</AlertText>
              </div>
            )}
        </div>

        {props.envOptions &&
          !!props.envOptions.length && (
            <div className="form-group">
              <Field
                className="env-select"
                component="select"
                name="env"
                onChange={onSelectChange}
              >
                {props.envOptions.map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </Field>
            </div>
          )}

        <div className="btn-container">
          <LoginButton disabled={isFormSubmitting}>Sign In</LoginButton>
        </div>
      </Form>
    </div>
  );
};

const StyledInnerForm = styled(InnerForm)`
  display: flex;
  flex-flow: column nowrap;
  align-items: center;
  justify-content: center;
  height: 100vh;

  .logo-container {
    padding: 15px;
    img {
      display: block;
      width: 10rem;
      margin: 0 auto;
      height: 100%;
    }
  }
  form {
    background: #ffffff;
    padding: 4rem 2rem;
    max-width: 840px;
    min-width: 450px;
    .form-header {
      margin-bottom: 35px;
      text-align: center;
    }
    .form-group {
      width: 100%;
      margin-bottom: 20px;
      min-height: 50px;
      position: relative;
      input {
        width: 100%;
        border: none;
        text-align: center;
        &:focus,
        &:active {
          outline: none;
        }
        ::placeholder {
          font-size: 0.9rem;
          color: rgba(0, 0, 0, 0.5);
          opacity: 1; /* Firefox */
          text-align: center;
        }
      }
      .env-select {
        width: 100%;
        height: 35px;
        background: inherit;
        border: 1px solid rgb(224, 224, 224);
        &:focus {
          outline-color: rgb(77, 134, 191);
        }
      }
      hr {
        bottom: 20px;
        box-sizing: content-box;
        margin: 0px;
        position: absolute;
        width: 100%;
      }
      .hr-bottom {
        border-top: none rgb(224, 224, 224);
        border-left: none rgb(224, 224, 224);
        border-right: none rgb(224, 224, 224);
        border-bottom: 1px solid rgb(224, 224, 224);
      }
      .error-hr-bottom {
        border-top: none rgb(230, 60, 60);
        border-left: none rgb(230, 60, 60);
        border-right: none rgb(230, 60, 60);
        border-bottom: 2px solid rgb(230, 60, 60);
        transform: scaleX(1);
        transition: all 450ms cubic-bezier(0.23, 1, 0.32, 1) 0ms;
      }
      .error-text {
        position: absolute;
        bottom: 0px;
        font-size: 0.8rem;
        width: 100%;
        text-align: center;
      }
    }
    .btn-container {
      width: 100%;
      text-align: center;
      input {
        width: 60%;
        height: 40px;
        padding: 0;
      }
    }
  }
`;

export interface LoginFormProps {
  message?: string;
  onSubmitClick?: any;
  email?: string;
  password?: string;
  env?: string;
  envOptions?: SelectOptions[];
  isFormSubmitting?: boolean;
}

const LoginForm = withFormik<LoginFormProps, FormValues>({
  mapPropsToValues(val) {
    return {
      email: val.email || '',
      password: val.password || '',
      env:
        val.env || (val.envOptions && val.envOptions.length > 0)
          ? val.envOptions[0].value
          : '',
      message: val.message || '',
      envOptions: val.envOptions || [],
      isFormSubmitting: val.isFormSubmitting,
    };
  },
  validate(values) {
    const errors: any = {};

    if (/^(.+)\@(.+)\.(.+)$/i.test(values.email.trim()) === false) {
      errors.email = 'Enter a valid email';
    }

    if (values.email && !values.password) {
      // tslint:disable-next-line
      errors.password = 'Password is required';
    }

    return errors;
  },
  handleSubmit(values: FormValues, opts) {
    if (
      opts.props &&
      opts.props.onSubmitClick &&
      typeof opts.props.onSubmitClick === 'function'
    ) {
      opts.props.onSubmitClick(values);
    }
  },
})(StyledInnerForm);

export default LoginForm;
